/**
 * KcRenewalBanner — Amber banner shown in Dashboard when the user's
 * KOMPA CLUB subscription is close to expiring (≤ leadDays, default 3)
 * or already expired.
 *
 * Uses the /api/kc/renewal-status endpoint (single source of truth,
 * rolling-window aware). Silently hides when the user doesn't have KC
 * or is nowhere near expiring.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ArrowRight } from 'lucide-react';
import api from '@/store/api';

export default function KcRenewalBanner({ onRenew }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api('/api/kc/renewal-status')
      .then((s) => {
        if (cancelled) return;
        setStatus(s);
        // Persist dismissal per expiration cycle (localStorage) so we don't
        // nag a user who already dismissed on this reload.
        try {
          const key = `kompa.kc_renewal_dismissed.${s.validUntil || 'none'}`;
          if (localStorage.getItem(key)) setDismissed(true);
        } catch (_) { /* private mode / no storage */ }
      })
      .catch(() => { /* silent — banner just won't render */ });
    return () => { cancelled = true; };
  }, []);

  const persistDismiss = () => {
    setDismissed(true);
    try {
      const key = `kompa.kc_renewal_dismissed.${status?.validUntil || 'none'}`;
      localStorage.setItem(key, String(Date.now()));
    } catch (_) { /* ignore */ }
  };

  if (!status || dismissed) return null;
  if (!status.needsRenewal) return null;

  const isExpired = status.expired;
  const daysLeft = status.daysLeft ?? 0;

  const title = isExpired
    ? 'Tu KOMPA CLUB expiró'
    : `Renueva tu KOMPA CLUB antes del vencimiento`;
  const subtitle = isExpired
    ? 'Reactívalo para volver a pagar tus cuotas sin interrupción.'
    : `Quedan ${daysLeft} día${daysLeft === 1 ? '' : 's'} · vence el ${formatEs(status.validUntil)}`;

  return (
    <AnimatePresence>
      <motion.div
        key="kc-renewal-banner"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.25 }}
        className="mx-6 md:mx-8 mb-4"
        data-testid="kc-renewal-banner"
      >
        <div className="relative overflow-hidden rounded-2xl border border-kompa-orange/30
                        bg-gradient-to-br from-kompa-orange-light to-white
                        shadow-sm">
          <div className="flex items-start gap-3 p-4">
            <div className="mt-0.5 flex-shrink-0 rounded-full bg-kompa-orange/15 p-2">
              <Clock className="h-5 w-5 text-kompa-orange" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-kompa-navy leading-tight">{title}</p>
              <p className="mt-1 text-xs text-gray-600 leading-snug">{subtitle}</p>
              <button
                type="button"
                onClick={onRenew}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full
                           bg-kompa-navy px-4 py-2 text-xs font-bold text-white
                           shadow-sm hover:bg-kompa-navy-hover transition-colors"
                data-testid="kc-renewal-banner-cta"
              >
                Renovar ahora
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              type="button"
              onClick={persistDismiss}
              className="flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Cerrar"
              data-testid="kc-renewal-banner-dismiss"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function formatEs(iso) {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
  } catch { return iso; }
}
